import React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import { Button, Modal } from 'components/ui';
import { MediaType } from 'types/AbstractMedia';
import { GateConfig } from 'modules/gates/types/Gate';
import useCountdown from 'hooks/useCountdown';
import { DEFAULT_MEDIA_COMPONENTS } from 'components/ui/File';
import { INACTIVITY_TIME_GATES } from 'modules/auth/constants';

interface GateAccessModalProps {
  config: GateConfig;
  onFinish: () => void;
}

const CONFIG: Partial<Record<GateConfig['type'], {
  title: MessageDescriptor;
  description: MessageDescriptor;
  content: (config: GateConfig) => React.ReactNode;
}>> = {
  PIN_CODE: {
    title: { id: 'gates.pinCode.title', defaultMessage: 'Gate PIN code' },
    description: {
      id: 'gates.pinCode.description',
      defaultMessage: 'Please enter this code on the gate’s keypad to open it.',
    },
    content: config => (
      <div className="text-center font-bold tracking-[10px] text-[20px] text-default bg-input-bg py-4 pr-4 pl-[26px] rounded-lg border border-border-default">
        {config.pinCode}
      </div>
    ),
  },
  QR_CODE: {
    title: { id: 'gates.qrCode.title', defaultMessage: 'Gate QR Code' },
    description: {
      id: 'gates.qrCode.description',
      defaultMessage: 'Please show this to the gate’s QR code reader.',
    },
    content: config => (
      <div className="p-4 bg-white rounded-lg border border-border-default">
        {config.qrCode && DEFAULT_MEDIA_COMPONENTS[MediaType.IMAGE](config.qrCode, { className: 'size-64' })}
      </div>
    ),
  },
};

const GateAccessModal: React.FC<GateAccessModalProps> = ({ config, onFinish }) => {
  const expirationDate = React.useMemo(() => Date.now() + INACTIVITY_TIME_GATES * 1000, []);
  const { countdown } = useCountdown({ date: expirationDate });

  React.useEffect(() => {
    if (countdown === 0) {
      onFinish();
    }
  }, [countdown, onFinish]);

  const { title, description, content } = React.useMemo(() => CONFIG[config.type]!, [config.type]);

  return (
    <Modal
      open
      title={<FormattedMessage {...title} tagName="h3" />}
      className="modal-large"
      hideCloseButton
    >
      <div className="flex flex-col items-center gap-6">
        <p className="text-center text-lg">
          <FormattedMessage {...description} />
        </p>
        {content(config)}

        <Button onClick={onFinish} className="w-full mt-4">
          <FormattedMessage id="steps.finish" defaultMessage="Finish" />
        </Button>
      </div>
    </Modal>
  );
};

export default GateAccessModal;